/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageProps } from "../../../models/PageProps";
import { getGeneralError } from "../../../util/helperFunctions";
import { Loader } from "../../../components/Loader";
import AlertBox from "../../../components/AlertBox";
import ClinicProfileApi from "../../../api/ClinicProfileApi";
import { ClinicProfileSettingsResponse } from "../../../models/clinic/profile/ClinicProfileSettingsResponse";
import { ClinicProfileVisibility } from "../../../models/clinic/profile/ClinicProfileVisibility";
import { getStringFromEnumsOrReturn, strings } from "../../../common/Strings/Strings";
import BasicSettings from "./settings/BasicSettings";
import Contacts from "./settings/Contacts";
import Employees from "./settings/Employees";
import ReservationTypes from "./settings/ReservationTypes";
import OpeningHours from "./settings/OpeningHours";
import SpecialOpeningHours from "./settings/SpecialOpeningHours";
import CustomSections from "./settings/CustomSections";
import ClinicApi from "../../../api/ClinicApi";
import { ClinicResponse } from "../../../models/clinic/ClinicResponse";

export const clinicProfileVisibilityOptions = Object.keys(ClinicProfileVisibility).map((i) => ({
  title: getStringFromEnumsOrReturn(i),
  value: i,
}));

const ClinicProfileSettings: React.FC<PageProps> = ({ setPageLoading }: PageProps) => {
  const [settings, setSettings] = useState<ClinicProfileSettingsResponse>();
  const [clinic, setClinic] = useState<ClinicResponse>();
  const [error, setError] = useState<string | null>(null);
  const { clinicId } = useParams<"clinicId">();
  const [settingsLoading, setSettingsLoading] = useState<boolean>(false);
  const [clinicLoading, setClinicLoading] = useState<boolean>(false);

  useEffect(() => {
    const getProfileSettings = async (id: string) => {
      try {
        setSettingsLoading(true);
        const response = await ClinicProfileApi.getClinicProfileSettings(id);
        setSettings(response.data);
      } catch (e) {
        setError(await getGeneralError(e));
      } finally {
        setSettingsLoading(false);
      }
    };

    const getClinic = async (id: string) => {
      try {
        setClinicLoading(true);
        const response = await ClinicApi.getClinic(id);
        setClinic(response.data);
      } catch (e) {
        setError(await getGeneralError(e));
      } finally {
        setClinicLoading(false);
      }
    };

    if (clinicId) {
      void getProfileSettings(clinicId);
      void getClinic(clinicId);
    }
  }, [clinicId]);

  useEffect(() => {
    setPageLoading(settingsLoading || clinicLoading);
  }, [settingsLoading, clinicLoading]);

  if (!settings && error) {
    return <AlertBox message={error} />;
  }

  if (!settings || !clinic) {
    return <></>;
  }

  return (
    <main className="main-signed-in">
      <section className="flex flex-col items-start md:items-center justify-center space-y-6 mx-auto px-4 md:px-0 py-10 md:py-14">
        <div>
          <h1 className="flex items-center text-xl font-semibold leading-tight text-zinc-800 lg:text-2xl dark:text-white">
            {clinic.name}
          </h1>
        </div>
        <div className="w-full md:tw-card md:max-w-2xl space-y-6 md:p-8">
          <div className="flex">
            <h2 className="text-lg font-semibold leading-tight text-zinc-800 lg:text-xl dark:text-white flex-1">
              {strings.clinicPublicProfile}
            </h2>
            <div>
              <Link to={`/clinics/${clinicId}`}>{strings.viewClinicProfile}</Link>
            </div>
          </div>
          <div>{strings.clinicProfileSettingsTooltip}</div>
          <BasicSettings clinic={clinic} settings={settings} setSettings={setSettings} />
          <Contacts clinic={clinic} settings={settings} setSettings={setSettings} />
          <Employees clinic={clinic} settings={settings} setSettings={setSettings} />
          <ReservationTypes clinic={clinic} settings={settings} setSettings={setSettings} />
          <OpeningHours clinic={clinic} settings={settings} setSettings={setSettings} />
          <SpecialOpeningHours clinic={clinic} settings={settings} setSettings={setSettings} />
          <CustomSections clinic={clinic} settings={settings} setSettings={setSettings} />
          <div>
            <Link to={`/clinics/${clinicId}`}>{strings.viewClinicProfile}</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Loader(ClinicProfileSettings);
